import { useCallback, useEffect, useState } from 'react';
import { APP_BUILD_ID, APP_UPDATE_POLL_MS, VERSION_JSON_PATH } from '../constants/appVersion';

/**
 * Poll deployed version.json and detect when a newer frontend build is live.
 * @returns {{ updateAvailable: boolean, remoteBuildId: string|null, dismiss: () => void, refresh: () => void }}
 */
export function useAppUpdateCheck({
  enabled = !import.meta.env.DEV,
  pollIntervalMs = APP_UPDATE_POLL_MS,
} = {}) {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [remoteBuildId, setRemoteBuildId] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  const checkForUpdate = useCallback(async () => {
    if (!enabled || dismissed) return;

    try {
      const response = await fetch(`${VERSION_JSON_PATH}?_=${Date.now()}`, {
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return;

      const data = await response.json();
      const remoteId = data?.buildId != null ? String(data.buildId) : '';
      if (!remoteId || remoteId === APP_BUILD_ID) return;

      setRemoteBuildId(remoteId);
      setUpdateAvailable(true);
    } catch {
      // Ignore transient network errors; we'll retry on the next poll.
    }
  }, [enabled, dismissed]);

  useEffect(() => {
    if (!enabled) return undefined;

    checkForUpdate();

    const intervalId = window.setInterval(checkForUpdate, pollIntervalMs);
    const onFocus = () => checkForUpdate();
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') checkForUpdate();
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [checkForUpdate, enabled, pollIntervalMs]);

  const dismiss = useCallback(() => {
    setDismissed(true);
    setUpdateAvailable(false);
  }, []);

  const refresh = useCallback(() => {
    window.location.reload();
  }, []);

  return {
    updateAvailable: updateAvailable && !dismissed,
    remoteBuildId,
    dismiss,
    refresh,
  };
}
